import React, { useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localeData from 'dayjs/plugin/localeData';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import weekYear from 'dayjs/plugin/weekYear';
import weekday from 'dayjs/plugin/weekday';
import { BarChart3, Calendar } from 'lucide-react';
import { Card, CardContent } from '../../../../components/ui/card';
import AdminLayout from '../../../../layouts/AdminLayout';
import DateRangePicker from './components/DateRangePicker';
import DepartmentsActivityChart from './components/DepartmentsActivityChart';
import EmployeesActivityChart from './components/EmployeesActivityChart';
import LinkViewsCard from './components/LinkViewsCard';
import RecentReviews from './components/RecentReviews';
import StatsCards from './components/StatsCards';

dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);

function AdminDashboardPageV3({ title }) {
  const [startDate, setStartDate] = useState(dayjs().subtract(1, 'month'));
  const [endDate, setEndDate] = useState(dayjs());

  const handleDateChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
  };

  return (
    <AdminLayout title={title}>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <BarChart3 className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">{title}</h1>
              <p className="text-muted-foreground text-sm">
                Overview of platform activity and statistics
              </p>
            </div>
          </div>

          {/* Date Range Picker */}
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <DateRangePicker startDate={startDate} endDate={endDate} onChange={handleDateChange} />
          </div>
        </div>

        {/* Stats Cards */}
        <StatsCards startDate={startDate} endDate={endDate} />

        {/* Activity Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <DepartmentsActivityChart startDate={startDate} endDate={endDate} />
          <EmployeesActivityChart startDate={startDate} endDate={endDate} />
        </div>

        {/* Most Visited Links */}
        <Card style={{ border: '1px solid hsl(var(--border))' }}>
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold mb-4">Most Visited Links</h3>
            <LinkViewsCard startDate={startDate} endDate={endDate} />
          </CardContent>
        </Card>

        {/* Recent Reviews */}
        <Card style={{ border: '1px solid hsl(var(--border))' }}>
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold mb-4">Recent Reviews</h3>
            <RecentReviews startDate={startDate} endDate={endDate} />
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}

AdminDashboardPageV3.propTypes = {
  title: PropTypes.string.isRequired,
};

export default AdminDashboardPageV3;
